import {marked} from 'marked'
import DOMPurify from 'dompurify'

// This config reflects that allowed by our server side filters, see https://github.com/github/github/blob/28257fc212b2f36422f7a151b4bac3b7828f0f06/lib/html/pipeline/sanitization_filter.rb#L40-L46
const DompurifyConfig = {
  FORBID_TAGS: ['style'], // Style should always be forbidden, otherwise it can lead to UI Redressing
  // Forbid class, id, and style attributes to prevent DOM XSS attacks where injected
  // class names (e.g., js-comment-edit-button) trigger GitHub's JS behavior hooks,
  // injected id attributes allow URL fragment targeting (e.g., #clickme), and
  // inline styles enable UI redressing/phishing (e.g., position:fixed overlays).
  // The server-side SanitizationFilter does not allow class, id, or style on any element.
  // See: https://github.com/github/issues/issues/19987
  FORBID_ATTR: ['class', 'id', 'style'],
  ALLOW_DATA_ATTR: false,
  // Always use ALLOWED_TAGS instead of ADD_TAGS to override the
  // default DOMPurify config, making only the tags explicitly
  // allowed by this config to be considered safe
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'h7',
    'h8',
    'br',
    'b',
    'i',
    'strong',
    'em',
    'a',
    'pre',
    'code',
    'img',
    'tt',
    'div',
    'span',
    'ins',
    'del',
    'sup',
    'sub',
    'p',
    'ol',
    'ul',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'blockquote',
    'dl',
    'dt',
    'dd',
    'kbd',
    'q',
    'samp',
    'var',
    'hr',
    'ruby',
    'rt',
    'rp',
    'li',
    'tr',
    'td',
    'th',
    's',
    'strike',
    'summary',
    'details',
    'picture',
    'source',
  ],
}

// This function checks if the markdown can be parsed optimistically
// Task lists can not be parsed optimistically since they require styling to avoid jitter
// and this is not allowed for security reasons
export function canBeOptimisticallyParsed(markdown: string): boolean {
  // Check for the presence of certain syntax that indicates the markdown can be parsed optimistically
  // Matches lines that start with - [ ] or - [x] (optionally *, +, or -), possibly with whitespace before
  const optimisticSyntax = /^\s*[-*+] \[[ xX]\] .*/m
  return !optimisticSyntax.test(markdown)
}

export function md2html(markdown: string): string {
  try {
    const dirtyHTML = marked.parse(markdown, {
      gfm: true,
      breaks: true,
      silent: true, // Prevents errors from being thrown for invalid markdown
    })
    return DOMPurify.sanitize(dirtyHTML, DompurifyConfig)
  } catch {
    // If there's an error in parsing, return an empty string
    return ''
  }
}
